import api from "./api";

export type ApiError =
  | { type: "serverValidation"; errors?: Record<string, string> }
  | { type: "network"; message?: string }
  | { type: "server"; message?: string };

export interface Currency {
  id: number;
  currency_name: string;
  currency_symbol: string;
  currency_code: string;
  exchange_rate: number;
  usd_price: number | null;
  is_cryptocurrency: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecentSale {
  id: number;
  transaction_id: string;
  company_id: number;
  user_id: number;
  location_id: number;
  date_time: string;
  status: string;
  booking_type: string;
  payment_gateway: string;
  original_amount: number;
  product_amount: number;
  discount: number;
  discount_percent: number;
  tax_amount: number;
  amount_to_pay: number;
  payment_status: string;
  source: string;
  additional_notes: string | null;
  created_at: string;
  updated_at: string;
  formated_amount_to_pay: string;
  user: {
    id: number;
    name: string;
    email: string;
    mobile: string | null;
    user_image_url: string;
    formatted_mobile: string;
  };
  location: {
    id: number;
    name: string;
  };
  company: {
    id: number;
    company_name: string;
    currency: Currency;
  };
  items?: Array<{
    id: number;
    business_service_id?: number | null;
    product_id?: number | null;
    sunbed_id?: number | null;
    employee?: {
      id: number;
      name: string;
      email?: string;
      mobile?: string;
    };
    business_service?: {
      id: number;
      name: string;
    } | null;
    product?: {
      id: number;
      name: string;
    } | null;
    sunbed?: {
      id: number;
      name?: string;
    } | null;
  }>;
}

/** Comma-separated service / product / sunbed names from booking line items. */
export function getBookingServicesLabel(
  items: RecentSale["items"] | unknown
): string {
  if (!Array.isArray(items) || items.length === 0) return "—";

  const names = items
    .map((item) => {
      const row = item as NonNullable<RecentSale["items"]>[number];
      if (row.business_service?.name?.trim()) return row.business_service.name.trim();
      if (row.product?.name?.trim()) return row.product.name.trim();
      if (row.sunbed?.name?.trim()) return row.sunbed.name.trim();
      return null;
    })
    .filter((name): name is string => Boolean(name));

  return names.length > 0 ? names.join(", ") : "—";
}


export interface DashboardTodayStats {
  revenue: number;
  bookings: number;
  walkIns: number;
  nextAppointment: {
    id: number | null;
    customer: string | null;
    time: string | null;
    services_label: string | null;
  } | null;
}

export interface DashboardResponse {
  totalBooking: number;
  assignedPendingBooking: number;
  pendingBooking: number;
  approvedBooking: number;
  inProgressBooking: number;
  completedBooking: number;
  canceledBooking: number;
  offlineBooking: number;
  onlineBooking: number;
  totalCustomers: number;
  totalServices: number;
  totalEmployees: number;
  topSaleLabels: string[];
  topSaleData: number[];
  stylistsChartData: {
    labels: string[];
    servicesData: number[];
    earningsData: number[];
  };
  totalEarnings: number;
  recentSales: RecentSale[];
  todayStats?: DashboardTodayStats;
}

export type DashboardChartPeriod = "today" | "week" | "month";

export const getDashboard = async (
  startDate: string,
  endDate: string,
  chartPeriod: DashboardChartPeriod = "week"
): Promise<DashboardResponse> => {
  const errors: Record<string, string> = {};

  if (!startDate) errors.startDate = "Start date is required";
  if (!endDate) errors.endDate = "End date is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.get(
      `/admin/dashboard?startDate=${startDate}&endDate=${endDate}&chartPeriod=${chartPeriod}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Dashboard loaded:", response.data);

    return response.data.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in getDashboard:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error("Failed to load dashboard:", err.response.data || err.message);

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface UpcomingBooking {
  id: string | number;
  customer: string;
  date: string;
  time: string;
  amount: number;
  services_count: number;
  staff?: string;
  services_label?: string;
  status?: string;
}
export interface UpcomingBookingsResponse {
  bookings: UpcomingBooking[];
}

export const getUpcomingBookings = async (
  date: string
): Promise<UpcomingBookingsResponse> => {
  const errors: Record<string, string> = {};

  if (!date) {
    errors.date = "Date is required";
  }

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.get(
      `/admin/upcoming-bookings?date=${date}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Upcoming bookings loaded:", response.data.data);

    const rawBookings = response.data?.data?.bookings ?? [];
    const bookings = Array.isArray(rawBookings)
      ? rawBookings.map((booking: Record<string, unknown>) => {
          const items = booking.items as Array<{
            employee?: { name?: string };
            business_service?: { name?: string };
            product?: { name?: string };
            sunbed?: { name?: string };
          }> | undefined;
          const users = booking.users as Array<{ name?: string }> | undefined;
          const user = booking.user as { name?: string } | undefined;
          const dateTime = String(booking.date_time ?? "");

          return {
            id: booking.id as string | number,
            customer: user?.name ?? "Unknown",
            date: dateTime.split("T")[0] ?? "",
            time: dateTime
              ? new Date(dateTime).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
              : "",
            amount: Number(booking.amount_to_pay ?? 0),
            services_count: items?.length ?? 0,
            staff:
              items?.find((item) => item.employee?.name)?.employee?.name ??
              users?.[0]?.name ??
              "Unassigned",
            services_label: getBookingServicesLabel(items),
            status: String(booking.status ?? ""),
          } satisfies UpcomingBooking;
        })
      : [];

    return { bookings };
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in getUpcomingBookings:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error("Failed to load upcoming bookings:", err.response.data || err.message);

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface RoleLoginResponse {
  message: string;
  redirect: string;
}

export const roleLogin = async (
  roleId: string
): Promise<RoleLoginResponse> => {
  const errors: Record<string, string> = {};
  if (!roleId) {
    errors.roleId = "Role ID is required";
  }

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.post(
      `/admin/role-login`,
      { role_id: roleId },
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Role login response:", response.data);

    return {
      message: response.data.message,
      redirect: response.data.redirect,
    };
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      console.error("Network error in roleLogin:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    console.error("Role login failed:", err.response.data || err.message);

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface EmployeeLoginResponse {
  message: string;
  redirect: string;
}

export const employeeLogin = async (): Promise<EmployeeLoginResponse> => {
  try {
    const response = await api.post(
      `/admin/employee-login`,
      {},
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Employee login switched:", response.data);

    return {
      message: response.data.message,
      redirect: response.data.redirect,
    };
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in employeeLogin:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error("Employee login switch failed:", err.response.data || err.message);

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};
